import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';
import { ABOUT_CONTENT, mergePageContent } from '../content/pageDefaults';

const AboutPage = ({ navigate }) => {
  const [page, setPage] = useState(ABOUT_CONTENT);

  useEffect(() => {
    api.getPageContent('about')
      .then((data) => {
        if (data?.content) setPage(mergePageContent(ABOUT_CONTENT, data.content));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-cream">
      <Navbar navigate={navigate} />

      <section className="relative gradient-bg text-white pt-28 pb-12 sm:pt-32 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6">{page.hero.title}</h1>
          <p className="text-base sm:text-xl md:text-2xl text-sand max-w-3xl mx-auto">
            {page.hero.subtitle}
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-ink mb-6">{page.story.title}</h2>
              {(page.story.paragraphs || []).map((paragraph, index, list) => (
                <p key={index} className={`text-lg text-ink/65 leading-relaxed ${index === list.length - 1 ? '' : 'mb-4'}`}>
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative">
              <img
                src={page.story.image}
                alt={page.story.title}
                className="w-full h-[240px] sm:h-[380px] lg:h-[500px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[page.mission, page.vision].map((block) => (
              <div key={block.title} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-6">
                  <span className="text-3xl">{block.icon}</span>
                </div>
                <h3 className="text-2xl font-bold text-ink mb-4">{block.title}</h3>
                <p className="text-ink/65 leading-relaxed">{block.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">{page.values.title}</h2>
            <p className="text-xl text-ink/65">{page.values.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(page.values.items || []).map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-20 h-20 bg-cream rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">{item.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-3">{item.title}</h3>
                <p className="text-ink/65">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(page.stats || []).map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl sm:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-sand">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">{page.team.title}</h2>
            <p className="text-xl text-ink/65">{page.team.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(page.team.members || []).map((member) => (
              <div key={member.name} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <img src={member.image} alt={member.name} className="w-full h-56 sm:h-80 object-cover" />
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-ink mb-2">{member.name}</h3>
                  <p className="text-primary font-semibold">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">{page.certifications.title}</h2>
            <p className="text-xl text-ink/65">{page.certifications.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(page.certifications.items || []).map((item) => (
              <div key={item.title} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-ink mb-2">{item.title}</h3>
                <p className="text-ink/65">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-ink mb-6">{page.cta.title}</h2>
          <p className="text-xl text-ink/65 mb-8">{page.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('category', { category: 'umrah-packages' })}
              className="bg-primary hover:bg-secondary text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105">
              {page.cta.primaryButton}
            </button>
            <button
              onClick={() => navigate('contact')}
              className="bg-sand hover:bg-accent/20 text-ink px-8 py-4 rounded-lg font-bold text-lg transition-all">
              {page.cta.secondaryButton}
            </button>
          </div>
        </div>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
};

export default AboutPage;
